import { SickLeaveScope, type SickLeaveConfig } from '../types/partner'

export type SickLeaveResolution =
    | { ok: true; emailTo: string[]; emailCc: string[] }
    | { ok: false; reason: 'not_configured' | 'not_allowed' | 'no_policy_holder' | 'duplicate_config' }

export function normalisePolicyHolder(value?: string | null): string {
    return (value ?? '').trim().toUpperCase()
}

function splitEmails(value?: string | null): string[] {
    return (value ?? '').split(',').map(e => e.trim()).filter(Boolean)
}

/**
 * Single source of truth for sick-leave routing, shared by the send modal and the send path.
 * Presence in a `per_policy_holder` list is what grants permission: there is no fallback to
 * the partner-level address, because a silent fallback re-creates the mis-routing this replaces.
 */
export function resolveSickLeaveRecipients(
    sickLeave: SickLeaveConfig | undefined,
    policyHolder?: string | null,
): SickLeaveResolution {
    if (!sickLeave) {
        return { ok: false, reason: 'not_configured' }
    }

    if (sickLeave.mode === SickLeaveScope.all) {
        const emailTo = splitEmails(sickLeave.email_to)
        if (!emailTo.length) {
            return { ok: false, reason: 'not_configured' }
        }
        return { ok: true, emailTo, emailCc: splitEmails(sickLeave.email_cc) }
    }

    // An unmigrated config carries no discriminator at all.
    if (sickLeave.mode !== SickLeaveScope.perPolicyHolder) {
        return { ok: false, reason: 'not_configured' }
    }

    const key = normalisePolicyHolder(policyHolder)
    if (!key) {
        return { ok: false, reason: 'no_policy_holder' }
    }

    const matches = (sickLeave.policy_holders ?? []).filter(entry => normalisePolicyHolder(entry.policy_holder) === key)

    if (!matches.length) {
        return { ok: false, reason: 'not_allowed' }
    }
    if (matches.length > 1) {
        return { ok: false, reason: 'duplicate_config' }
    }

    const emailTo = splitEmails(matches[0].email_to)
    if (!emailTo.length) {
        return { ok: false, reason: 'not_configured' }
    }

    return { ok: true, emailTo, emailCc: splitEmails(matches[0].email_cc) }
}
